import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { ReactElement } from 'react'
import { useState } from 'react'
import { GAINS_EXEMPTION_ASSUMPTION } from '@/domain/rollup'
import { PRICES_SOURCE_ID } from '@/domain/valuation'
import { formatMinor } from '~/api/client'
import type { Measure } from '~/charts/NetWorthChart'
import {
  DEFAULT_VISIBLE,
  HATCH,
  MEASURES,
  NetWorthChart,
  PARTIAL_COVERAGE_FILL,
  measuresFor,
} from '~/charts/NetWorthChart'
import type { NetWorthSnapshot } from '~/charts/data'
import {
  fetchNetWorthSeries,
  fetchNetWorthSnapshot,
  fetchSellableNetWorthSeries,
  isNoData,
} from '~/charts/data'
import { formatSignedMinor } from '~/charts/format'
import type { CoverageGap, NetWorthPoint, NetWorthSeries, ProvenanceSpan, SourceBeginning } from '~/charts/series'
import {
  DEFAULT_RANGE,
  RANGE_PRESETS,
  arrivalsIn,
  changeOverRange,
  coverageBoundaries,
  fxCarriedWithin,
  materialGaps,
  provenanceOf,
  provenanceSpans,
  rangesIndistinguishable,
  sliceRange,
  staleSourcesWithin,
  unpricedProjectionDays,
} from '~/charts/series'
import { chartCss, seriesColor } from '~/charts/theme'
import { SETTINGS_PATH } from '~/navigation'
import { Card, CardHead, ChartCard, StatCard } from '~/components/ui/Card'
import type { KeyTexture } from '~/components/ui/Controls'
import { Button, Filter, SegmentedControl, SeriesToggle } from '~/components/ui/Controls'
import { DeltaChip, Figure, directionOf } from '~/components/ui/Figure'
import type { ChipTone, Notice } from '~/components/ui/Status'
import { NoticeList, SourceRow } from '~/components/ui/Status'
import { DataTable, Swatch } from '~/components/ui/Table'
import { collectionControl, useCollectionStanding, useRequestCollection } from '~/state/collections'
import { useSources } from '~/state/sources'

const PAGE_CSS = `
/* A row that lays out three typed things and sets no type of its own: the
   figure, the chip and the since-note each read their own token, so a size here
   would be a fourth opinion none of them asks for. It inherits the body's 14/21
   and nothing renders at it. */
.nw-dashboard .nw-headline { align-items: baseline; display: flex; flex-wrap: wrap; gap: var(--nw-space-3) 14px; margin-top: var(--nw-space-2); }
.nw-dashboard .nw-headline-note { color: var(--nw-muted); font-size: var(--nw-text-cell); margin-top: 10px; }
.nw-dashboard .nw-headline-since { color: var(--nw-muted); font-size: var(--nw-text-cell); }
.nw-dashboard .nw-change-coverage, .nw-dashboard .nw-headline-scope { display: block; margin-top: var(--nw-space-1); }
.nw-dashboard .nw-hero-head { align-items: flex-start; display: flex; flex-wrap: wrap; gap: var(--nw-space-4); justify-content: space-between; }
.nw-dashboard .nw-hero-controls { align-items: flex-end; display: flex; flex-direction: column; gap: var(--nw-space-2); }
.nw-dashboard .nw-hero-plot { margin-top: 22px; }
.nw-dashboard .nw-range-note { margin: 0 0 var(--nw-space-3); }
.nw-dashboard .nw-stats { display: flex; flex-wrap: wrap; gap: var(--nw-space-6); }
.nw-dashboard .nw-stats > * { flex: 1 1 200px; }
.nw-dashboard .nw-stat-warn { color: var(--nw-critical); display: block; margin-top: var(--nw-space-1); }
.nw-dashboard .nw-connect {
  align-items: center; border: var(--nw-hairline-width) dashed var(--nw-hairline); border-radius: var(--nw-radius-md);
  color: var(--nw-accent); display: flex; font-size: var(--nw-text-cell); font-weight: var(--nw-weight-strong);
  justify-content: center; margin-top: var(--nw-space-3); min-height: var(--nw-tap-min); text-decoration: none;
}
.nw-dashboard .nw-connect:hover { background: var(--nw-hover); }
.nw-dashboard .nw-source:last-of-type { border-bottom: 0; }
/* Before anything has collected, this card is the page. */
.nw-dashboard .nw-first-actions { display: flex; flex-wrap: wrap; gap: var(--nw-space-2); }
.nw-dashboard .nw-first-note { color: var(--nw-muted); font-size: var(--nw-text-cell); line-height: 1.5; margin: var(--nw-space-3) 0 0; max-width: 72ch; }
.nw-dashboard .nw-first-note--refused { color: var(--nw-critical); }
`

const VIEWS = [
  { id: 'chart', label: 'Chart' },
  { id: 'table', label: 'Table' },
]

type Holdings = NonNullable<NetWorthSeries['holdings']>

const HOLDINGS: readonly { id: Holdings; label: string }[] = [
  { id: 'all', label: 'Included' },
  { id: 'sellable', label: 'Left out' },
]

export function Dashboard(): ReactElement {
  const [rangeId, setRangeId] = useState<string>(DEFAULT_RANGE)
  const [view, setView] = useState<'chart' | 'table'>('chart')
  const [visible, setVisible] = useState<ReadonlySet<string>>(DEFAULT_VISIBLE)
  const [holdingsChosen, setHoldingsChosen] = useState<Holdings>('all')

  const toggleSeries = (key: string): void =>
    setVisible((was) => {
      const next = new Set(was)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const everything = useQuery({ queryKey: ['networth', 'series'], queryFn: fetchNetWorthSeries, retry: false })
  // Offered on what every holding says: the sellable view holds nothing that
  // cannot be sold by construction, so it cannot say whether to offer itself.
  // Asked for only while offered, so a household that has sold the last of it
  // is not left on a view that leaves out nothing.
  const offersSellable = everything.data?.holdsUnsellable === true
  const holdings = offersSellable ? holdingsChosen : 'all'
  const series = useQuery({
    // A key of its own: Risk and Allocation read `['networth', 'series']` as every holding.
    queryKey: holdings === 'sellable' ? ['networth', 'series', { holdings }] : ['networth', 'series'],
    queryFn: holdings === 'sellable' ? fetchSellableNetWorthSeries : fetchNetWorthSeries,
    retry: false,
    // The last view stays up, dimmed, while the next is fetched, rather than
    // the page emptying to "Loading…" at every press.
    placeholderData: keepPreviousData,
  })
  // Where the view asked for could not be made, every holding stands in,
  // labelled by its own `holdings`, so the control that asked stays to undo it.
  const valued = series.data ?? everything.data
  // Named by the view the figures were built under rather than by the button,
  // which runs ahead of them while the other view is fetched.
  const sellableView = valued?.holdings === 'sellable'
  // No banner says so, because nothing failed: the view counts sources none of
  // which has reported yet, so it has no day to value. Without a line the press
  // would change nothing on screen and say nothing about why.
  const nothingSellableYet = holdings === 'sellable' && series.isError && isNoData(series.error)
  const snapshot = useQuery({
    queryKey: ['networth', 'current'],
    queryFn: fetchNetWorthSnapshot,
    retry: false,
  })

  // Pricing writes a verdict alongside every real source's, so its own failure
  // is not summarised away — but it collects nothing of anybody's, and a row
  // named "prices" beside a bank or a broker would be read as one.
  const sources = (snapshot.data?.sources ?? []).filter(
    (source) => source.source !== PRICES_SOURCE_ID,
  )

  const allPoints = valued?.points ?? []
  const points = sliceRange(allPoints, rangeId)
  const tooLittleHistory = rangesIndistinguishable(allPoints.length)
  const spans = provenanceSpans(points)
  const begins = valued?.coverage?.begins ?? []
  const arrivals = arrivalsIn(coverageBoundaries(points), begins)
  // Keyed and noticed only where the plot draws it. A stretch too small to draw
  // still has its arrival quoted, and its sources counted in the tooltip and
  // the table.
  const gaps = materialGaps(points, arrivals)
  const baseCurrency = valued?.baseCurrency ?? 'DKK'
  const latest = points[points.length - 1]

  // Spendable is only a second answer where a second entity holds something for
  // the tax to be charged against; without one it is look-through under another
  // name, explained by a structure the reader does not have.
  const holdsCompany = valued?.holdsThroughCompany ?? false
  // Liquid likewise, where nothing held cannot be sold: it takes nothing away
  // from the line before it and would be drawn on top of it (PRO-267).
  const holdsUnsellable = valued?.holdsUnsellable ?? false
  const measures = measuresFor({ company: holdsCompany, unsellable: holdsUnsellable })
  const shown = new Set(
    [...visible].filter((key) => measures.some((measure) => measure.key === key)),
  )

  // A tax of zero is either a bill of nothing or a bill nobody could compute,
  // and only the day the figure was made on knows which — but rates nobody set
  // change nothing for a household with no company to charge them against.
  const ratesMissing = holdsCompany && latest?.taxRatesConfigured === false

  const [headline] = MEASURES
  // What arrived is measured in look-through alone, and taken out of nothing else.
  const change = headline
    ? changeOverRange(
        points,
        (point) => point[headline.key],
        headline.key === 'lookThroughMinor' ? begins : [],
      )
    : undefined

  return (
    <main className="nw nw-dashboard">
      <style>{chartCss + PAGE_CSS}</style>
      <h1>Net worth</h1>
      <p className="nw-sub">
        {latest ? `Latest snapshot ${latest.date}` : 'Three ways of counting the same holdings'}
      </p>

      {series.isError && !isNoData(series.error) && (
        <p className="nw-flag nw-flag--missing">
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>Could not load the net worth series: {series.error.message}</span>
        </p>
      )}

      {everything.isError && isNoData(everything.error) && <FirstCollection />}

      {everything.isPending && <p className="nw-empty">Loading…</p>}

      {valued && headline && (
        <div className="nw-cols">
          <div className="nw-col-main nw-stack">
            <Card>
              <div className="nw-hero-head">
                <div>
                  <div className="nw-stat-label">
                    <span>{sellableView ? `${headline.label} · illiquid left out` : headline.label}</span>
                  </div>
                  <div className="nw-headline">
                    {latest ? (
                      <Figure
                        amountMinor={latest[headline.key]}
                        currency={baseCurrency}
                        size="display"
                      />
                    ) : (
                      '—'
                    )}
                    {change && (
                      <>
                        <DeltaChip direction={directionOf(change.changeMinor)}>
                          {formatSignedMinor(change.changeMinor, baseCurrency)}
                        </DeltaChip>
                        <span className="nw-headline-since">
                          since {change.from}
                          {change.arrivedMinor !== undefined && change.arrivedMinor !== 0 && (
                            <>
                              , not counting {change.arrivedMinor < 0 && 'a debt of '}
                              {formatMinor(Math.abs(change.arrivedMinor), baseCurrency)} that arrived
                              with {change.arrivedWith.join(', ')}
                            </>
                          )}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="nw-headline-note">
                    {sellableView ? leftOutNote(valued.leftOut, baseCurrency) : headline.note}
                    {nothingSellableYet && (
                      <span className="nw-headline-scope">
                        Nothing that can be sold has reported yet, so every holding is shown.
                      </span>
                    )}
                    {/* In the sellable view too: what it says it left out is
                        measured against the figure Settings already shrank. */}
                    {valued.includePrivate === false && (
                      <span className="nw-headline-scope">
                        Private holdings are left out of this figure in{' '}
                        <a href={SETTINGS_PATH}>Settings</a>.
                      </span>
                    )}
                    {change &&
                      change.joinedSources.length > 0 &&
                      change.arrivedMinor === undefined && (
                        <span className="nw-change-coverage">
                          {change.joinedSources.join(', ')} first reported inside this range, so the
                          two ends of it are not the same set of sources
                        </span>
                      )}
                  </div>
                </div>
                <div className="nw-hero-controls">
                  {offersSellable && (
                    <Filter
                      label="Illiquid holdings"
                      choices={HOLDINGS}
                      value={holdings}
                      onChange={(id) => setHoldingsChosen(id === 'sellable' ? 'sellable' : 'all')}
                    />
                  )}
                  <SegmentedControl
                    label="Range"
                    choices={RANGE_PRESETS.map((preset) => ({ id: preset.id, label: preset.label }))}
                    value={rangeId}
                    onChange={setRangeId}
                  />
                </div>
              </div>

              <div className="nw-hero-plot">
                <ChartCard
                  label="Net worth over time"
                  aside={
                    <SegmentedControl
                      label="Net worth over time view"
                      choices={VIEWS}
                      value={view}
                      onChange={(id) => setView(id === 'table' ? 'table' : 'chart')}
                    />
                  }
                >
                  {tooLittleHistory && <RangeHistoryNote days={allPoints.length} />}
                  {view === 'chart' ? (
                    <div className={series.isFetching ? 'nw-plot nw-plot--loading' : 'nw-plot'}>
                      <NetWorthChart
                        points={points}
                        baseCurrency={baseCurrency}
                        visible={shown}
                        measures={measures}
                        arrivals={arrivals}
                      />
                    </div>
                  ) : (
                    <PointsTable points={points} baseCurrency={baseCurrency} measures={measures} />
                  )}
                </ChartCard>
                <SeriesToggle
                  label="Series"
                  series={measures.map((measure) => ({
                    id: measure.key,
                    label: measure.label,
                    stroke: measure.stroke,
                    color: measure.color,
                    title: measure.note,
                  }))}
                  visible={shown}
                  onToggle={toggleSeries}
                  extras={shadings(spans, gaps)}
                />
              </div>
            </Card>

            {/* The headline is already the card above; repeating it here would
                be the same figure twice on one screen. */}
            <div className="nw-stats">
              {measures.filter((measure) => measure.key !== headline.key).map((measure) => (
                <MeasureStat
                  key={measure.key}
                  measure={measure}
                  points={points}
                  baseCurrency={baseCurrency}
                  untaxed={ratesMissing && measure.key !== 'lookThroughMinor'}
                />
              ))}
            </div>
          </div>

          <div className="nw-col-side">
            <Card pad="tight">
              <CardHead title="Sources" />
              <Sources sources={sources} />
              <a className="nw-connect" href="/sources">
                Connect a bank, broker or wallet
              </a>
            </Card>

            <Card pad="tight">
              <CardHead title="Worth a look" />
              <WorthALook
                points={points}
                spans={spans}
                gaps={gaps}
                arrivals={arrivals}
                sources={sources}
                baseCurrency={baseCurrency}
                ratesMissing={ratesMissing}
                taxMinor={latest?.taxMinor ?? 0}
                holdsCompany={holdsCompany}
              />
            </Card>
          </div>
        </div>
      )}
    </main>
  )
}

/**
 * What stands under the sellable view's figure in place of "Everything you
 * own", which is what it is not. What it is short of is what a reader turned
 * it on to see, and where that is a debt this figure is the higher one, which
 * a plain "leaves out" would leave unexplained.
 */
function leftOutNote(leftOut: NetWorthSeries['leftOut'], currency: string): string {
  if (leftOut === undefined) return 'Only what can be sold; what cannot comes to nothing today.'
  const amount = formatMinor(Math.abs(leftOut.lookThroughMinor), currency)
  return leftOut.lookThroughMinor < 0
    ? `Leaves out ${amount} owed against what cannot be sold, so this reads above the whole figure.`
    : `Leaves out ${amount} that cannot be sold.`
}

/**
 * The whole of this page before anything has been collected.
 *
 * "No snapshot has been taken yet, so there is nothing to plot" was all of it,
 * on a household that had just connected a bank, a broker and a wallet. True,
 * and silent about the only two things worth knowing at that moment: whether a
 * collection is under way, and what to press if it is not.
 */
function FirstCollection(): ReactElement {
  const sources = useSources()
  const standing = useCollectionStanding()
  const request = useRequestCollection()
  const control = collectionControl(standing.data)
  const queued = standing.data?.queued === true
  const refusal = request.error?.message

  // A household with nothing connected has nothing to collect, and a control
  // that queues a run reaching no provider is a press that changes nothing and
  // says nothing about why.
  const collectable =
    standing.data?.available === true &&
    (sources.data?.sources ?? []).some((source) => source.configured)

  return (
    <Card>
      <CardHead
        title={queued ? 'Collecting now' : 'No snapshot has been taken yet'}
        lede={
          // Which sources are being read is the control's own sentence below,
          // because a collection is now sometimes one source and sometimes the
          // household — and a promise about what happens on connecting has to
          // be the promise the connect keeps.
          queued
            ? 'A collection is running. This page fills in on its own as it finishes.'
            : 'Nothing has been collected, so there is nothing to plot. Every source collects as soon as it is connected, and anything already connected can be asked to report again.'
        }
      />
      <div className="nw-first-actions">
        <a className="nw-btn" href="/sources">
          Connect a bank, broker or wallet
        </a>
        {collectable && (
          <Button
            disabled={control.disabled || request.isPending}
            onClick={() => request.mutate()}
          >
            {request.isPending ? 'Asking…' : control.label}
          </Button>
        )}
      </div>
      {collectable && (refusal ?? control.note) !== '' && (
        <p className={refusal === undefined ? 'nw-first-note' : 'nw-first-note nw-first-note--refused'} role="status">
          {refusal ?? control.note}
        </p>
      )}
    </Card>
  )
}

/**
 * What every range preset draws when there is not yet more history than the
 * narrowest of them holds: the same days, however many times the control is
 * pressed. Said here rather than left silent, so that looks like a household
 * early in its history rather than a control that stopped working — and said
 * again, differently, the day a range finally has something to narrow.
 */
function RangeHistoryNote({ days }: { days: number }): ReactElement {
  const history = days === 1 ? 'one day of history' : `${days} days of history`
  return (
    <p className="nw-note nw-range-note">
      There is only {history} yet, so every range above draws the same days. This updates on its
      own as more is collected.
    </p>
  )
}

/**
 * The three shadings the plot also carries, each keyed in the plot's own
 * texture. They are not series and cannot be switched off: a day nobody
 * confirmed, and a day missing a source, are facts about the figure, not layers
 * a reader chose to see.
 */
function shadings(
  spans: readonly ProvenanceSpan[],
  gaps: readonly CoverageGap[],
): { label: string; color: string; texture: KeyTexture; title: string }[] {
  const keys: { label: string; color: string; texture: KeyTexture; title: string }[] = []
  if (gaps.length > 0) {
    keys.push({
      label: beforeBegan([...new Set(gaps.flatMap((gap) => gap.missing))].sort()),
      color: PARTIAL_COVERAGE_FILL,
      texture: 'flat',
      title: 'A source had no history yet, so the figure covers less of the household.',
    })
  }
  if (spans.some((span) => span.kind === 'modelled')) {
    keys.push({
      label: 'Modelled, not fetched',
      color: HATCH.modelled.color,
      texture: HATCH.modelled.texture,
      title: 'Part of the figure rests on positions no source confirmed.',
    })
  }
  if (spans.some((span) => span.kind === 'carriedForward')) {
    keys.push({
      label: 'Carried forward',
      color: HATCH.carriedForward.color,
      texture: HATCH.carriedForward.texture,
      title: 'A source stopped answering; its last known positions are repeated.',
    })
  }
  return keys
}

/**
 * "Fewer sources" said the days were short and not of what, so a sliver missing
 * read like most of the household. Past three names a key starts wrapping.
 */
function beforeBegan(sources: readonly string[]): string {
  return sources.length <= 3
    ? `Before ${sources.join(', ')} began`
    : "Before all of today's sources began"
}

function MeasureStat({
  measure,
  points,
  baseCurrency,
  untaxed,
}: {
  measure: Measure
  points: readonly NetWorthPoint[]
  baseCurrency: string
  untaxed: boolean
}): ReactElement {
  const latest = points[points.length - 1]
  const change = changeOverRange(points, (point) => point[measure.key])
  const note = untaxed ? 'Before tax — no rates for this date.' : measure.note

  return (
    <StatCard
      label={measure.label}
      help={note}
      note={
        <>
          {change && (
            <span className="nw-measure">
              <DeltaChip direction={directionOf(change.changeMinor)}>
                {formatSignedMinor(change.changeMinor, baseCurrency)}
              </DeltaChip>
              <span>since {change.from}</span>
            </span>
          )}
          {/* Said in full rather than hidden behind the help dot: a figure that
              is not what its label claims has to say so where it is read. */}
          {untaxed && <span className="nw-stat-warn">{note}</span>}
          {change && change.joinedSources.length > 0 && (
            <span className="nw-change-coverage">
              {change.joinedSources.join(', ')} first reported inside this range, so the two ends of
              it are not the same set of sources
            </span>
          )}
        </>
      }
    >
      <span className="nw-measure">
        <Swatch color={seriesColor(measure.slot)} />
        {latest ? (
          <Figure amountMinor={latest[measure.key]} currency={baseCurrency} size="figure" />
        ) : (
          '—'
        )}
      </span>
    </StatCard>
  )
}

/**
 * A source that is answering, one that is repeating itself, and one that failed
 * are three different states, and only the first of them means the figures above
 * are current. A source nobody has configured is not listed: it has nothing to
 * say about today, and a row saying so would be the longest list on the page.
 */
function Sources({ sources }: { sources: NetWorthSnapshot['sources'] }): ReactElement {
  const listed = sources.filter((source) => source.status !== 'not_configured')
  if (listed.length === 0) {
    return <p className="nw-note">No source has reported yet.</p>
  }

  return (
    <>
      {listed.map((source) => (
        <SourceRow
          key={source.source}
          initials={source.source.slice(0, 2).toUpperCase()}
          name={source.source}
          detail={detailOf(source)}
          tone={toneOf(source.status)}
          label={labelOf(source.status)}
        />
      ))}
    </>
  )
}

/**
 * A fetch that failed is not the same as a source that has gone quiet. The
 * first still has yesterday's positions standing behind it, which is a caution;
 * the second has stopped reporting, which is a failure however recently it
 * broke. The words a reader sees are the labels beside this, not these.
 */
function toneOf(status: NetWorthSnapshot['sources'][number]['status']): ChipTone {
  if (status === 'ok') return 'live'
  if (status === 'error') return 'caution'
  return 'failed'
}

function labelOf(status: NetWorthSnapshot['sources'][number]['status']): string {
  if (status === 'ok') return 'Live'
  if (status === 'error') return 'Delayed'
  return 'Stale'
}

function detailOf(source: NetWorthSnapshot['sources'][number]): string {
  if (source.error) return source.error
  if (source.staleSince) return `Last reported ${source.staleSince}`
  return 'Reporting'
}

/**
 * Every qualification the figures above carry, as a list rather than as a stack
 * of full-width banners. Four of those on one page read as an outage, and none
 * of these is one — they are the reasons a number is less certain than it looks.
 */
function WorthALook({
  points,
  spans,
  gaps,
  arrivals,
  sources,
  baseCurrency,
  ratesMissing,
  taxMinor,
  holdsCompany,
}: {
  points: readonly NetWorthPoint[]
  spans: readonly ProvenanceSpan[]
  gaps: readonly CoverageGap[]
  arrivals: readonly SourceBeginning[]
  sources: NetWorthSnapshot['sources']
  baseCurrency: string
  ratesMissing: boolean
  taxMinor: number
  holdsCompany: boolean
}): ReactElement {
  const stalled = sources.filter((source) => source.status === 'carried_forward')
  const unpricedDays = unpricedProjectionDays(points)
  const notices: Notice[] = []

  // A size, not a signed amount: an unconfirmed debt is as far from fetched as
  // an asset, and "up to" a negative figure reads as the opposite of what it is.
  const peakWithin = (span: ProvenanceSpan, part: 'modelledMinor' | 'carriedMinor'): number =>
    points
      .filter((point) => point.date >= span.from && point.date <= span.to)
      .reduce((most, point) => Math.max(most, Math.abs(provenanceOf(point)[part])), 0)

  if (ratesMissing) {
    notices.push({
      id: 'tax',
      tone: 'critical',
      text: (
        <>
          <b>No tax rates for this date</b> — nothing has been subtracted for getting the company's
          money out, so Spendable is the figure before tax rather than the honest one.{' '}
          <a href={SETTINGS_PATH}>Set the rates in Settings</a>, from a date at or before this one.
        </>
      ),
    })
  }

  for (const span of spans.filter((candidate) => candidate.kind === 'modelled')) {
    notices.push({
      id: `modelled-${span.from}`,
      tone: 'warning',
      text: (
        <>
          <b>Modelled</b> — {span.from} to {span.to} ({span.days} days) the figure includes up to{' '}
          {formatMinor(peakWithin(span, 'modelledMinor'), baseCurrency)} resting on positions no
          source confirmed.
        </>
      ),
    })
  }

  for (const span of spans.filter((candidate) => candidate.kind === 'carriedForward')) {
    const carried = peakWithin(span, 'carriedMinor')
    notices.push({
      id: `stale-${span.from}`,
      tone: 'critical',
      text: (
        <>
          <b>Carried forward</b> — {span.from} to {span.to} ({span.days} days).{' '}
          {staleReason(span, points, stalled)}
          {carried > 0 && (
            <>
              {' '}
              Up to {formatMinor(carried, baseCurrency)} of the figure repeats positions from before
              these days rather than dropping them.
            </>
          )}
        </>
      ),
    })
  }

  for (const gap of gaps) {
    notices.push({
      id: `coverage-${gap.from}`,
      tone: 'neutral',
      text: (
        <>
          <b>{beforeBegan(gap.missing)}</b> — {gap.from} to {gap.to} ({gap.days} days): no history
          from {gap.missing.join(', ')} yet, so these days show less of what you own, not less money.
        </>
      ),
    })
  }

  /**
   * What arrived is quoted, because a count of sources does not tell anybody
   * whether to care: three sources arriving with twenty-five million and one
   * arriving with a picture off the wall are the same "1 of 6" otherwise, and
   * only the reader can say which of those changes the story.
   */
  const byDate = new Map<string, SourceBeginning[]>()
  for (const arrival of arrivals) {
    byDate.set(arrival.date, [...(byDate.get(arrival.date) ?? []), arrival])
  }
  for (const [date, began] of byDate) {
    notices.push({
      id: `begins-${date}`,
      tone: 'neutral',
      text: (
        <>
          {began.map((source) => source.source).join(', ')} first reported on {date}, and everything
          they held entered the figure on that one day:{' '}
          <b>
            {formatMinor(
              began.reduce((total, source) => total + source.lookThroughMinor, 0),
              baseCurrency,
            )}
          </b>{' '}
          of look-through arrived with them rather than being earned.
        </>
      ),
    })
  }

  if (unpricedDays > 0) {
    notices.push({
      id: 'unpriced',
      tone: 'warning',
      text: (
        <>
          On {unpricedDays} of these days an unconfirmed position could not be priced. It is left out
          of every line above rather than counted as zero.
        </>
      ),
    })
  }

  /**
   * What Spendable assumed, rather than only what it subtracted. The exemption
   * is a Danish rule nothing asked the household about, and a figure that is too
   * high for that reason looks exactly like one that is right.
   *
   * It hangs below the list rather than joining it because it qualifies no day
   * and names no fault: every notice above is a thing that went differently from
   * expected, and a standing assumption filed among them would read as one more
   * outage.
   */
  const spendableNote = holdsCompany && !ratesMissing && (
    <p className="nw-note" style={{ marginBottom: 0, paddingTop: 'var(--nw-space-3)' }}>
      Spendable subtracts an estimated {formatMinor(taxMinor, baseCurrency)} of tax on getting the
      company's money out. {GAINS_EXEMPTION_ASSUMPTION} It is an estimate for orientation, not tax
      advice.
    </p>
  )

  // The clean range used to return before this note, so the assumption was
  // withheld from precisely the household with nothing else to worry about.
  if (notices.length === 0) {
    return (
      <>
        <p className="nw-note">
          Every figure above was fetched, on every day in this range. Nothing is carried forward and
          nothing is modelled.
        </p>
        {spendableNote}
      </>
    )
  }

  return (
    <>
      <NoticeList notices={notices} />
      {spendableNote}
    </>
  )
}

/**
 * Why these days are older than they look, said apart rather than guessed.
 *
 * A source that stopped answering and a rate the feed had not published are
 * one flag by the time they reach here, so each is read off the stretch's own
 * days. The sources list answers for today: read for every stretch, it blamed
 * today's outage for a stretch months old, and blamed a broker that answered an
 * hour ago for days whose only fault was a rate the ECB had not published yet.
 */
function staleReason(
  span: ProvenanceSpan,
  points: readonly NetWorthPoint[],
  stalled: NetWorthSnapshot['sources'],
): string {
  const today = points[points.length - 1]?.date
  const quiet = staleSourcesWithin(points, span).map((name) => {
    // Its date and error are today's, so they belong only to a stretch still running today.
    const now = span.to === today ? stalled.find((source) => source.source === name) : undefined
    if (!now) return `${name} had stopped answering`
    return (
      `${name} has not reported since ${now.staleSince ?? 'an unknown date'}` +
      (now.error ? ` (${now.error})` : '')
    )
  })
  // Both can be true at once, and a household with a quiet broker *and* an
  // unpublished rate is owed both sentences rather than whichever was checked
  // first.
  if (fxCarriedWithin(points, span)) {
    quiet.push(
      'an exchange rate published before these days was used, so the conversion is older than the figure',
    )
  }
  if (quiet.length > 0) return `${quiet.join('; ')}.`
  return 'The last known positions are repeated rather than counted as a loss.'
}

function PointsTable({
  points,
  baseCurrency,
  measures,
}: {
  points: readonly NetWorthPoint[]
  baseCurrency: string
  measures: readonly Measure[]
}): ReactElement {
  // The newest day in view, because that is the figure every row is read against.
  const knownSources = points[points.length - 1]?.coverage?.sources.length ?? 0

  return (
    <div className="nw-table-wrap">
      <DataTable
        dense
        label={`Net worth by day, in ${baseCurrency}`}
        columns={[
          { id: 'date', label: 'Date' },
          ...measures.map((measure) => ({
            id: measure.key,
            label: measure.label,
            width: '160px',
            align: 'right' as const,
          })),
          { id: 'sources', label: 'Sources', width: '90px', align: 'right' as const },
          { id: 'data', label: 'Data', width: '150px' },
        ]}
      >
        {[...points].reverse().map((point) => {
          const provenance = provenanceOf(point)
          return (
            <tr key={point.date} data-flagged={provenance.flagged}>
              <td>{point.date}</td>
              {measures.map((measure) => (
                <td className="nw-num" key={measure.key}>
                  <Figure amountMinor={point[measure.key]} currency={baseCurrency} size="cell" />
                </td>
              ))}
              <td className="nw-num" title={point.coverage?.sources.join(', ')}>
                {point.coverage?.sources.length ?? 0} of {knownSources}
              </td>
              <td>
                {provenance.modelled && 'Modelled'}
                {provenance.modelled && provenance.carriedForward && ' · '}
                {provenance.carriedForward && 'Carried forward'}
                {!provenance.flagged && 'Fetched'}
              </td>
            </tr>
          )
        })}
      </DataTable>
    </div>
  )
}
